import { jsPDF } from 'jspdf'
import type { EventoDetalhe, EventoStats, ParticipanteEvento } from './types'
import { METODO_PAGAMENTO_LABEL } from './constants'
import { formatCurrency } from '@/lib/utils'
import { formatDate } from './utils'
import { getEventoSlug } from '@/lib/eventos'

export async function exportParticipantsToPdf(
  evento: EventoDetalhe,
  participantes: ParticipanteEvento[],
  stats: EventoStats
) {
  const fimEm = evento.fim_em
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  const marginX = 18
  const headerHeight = 32

  const drawHeader = () => {
    doc.setFillColor(36, 99, 235)
    doc.rect(0, 0, pageWidth, 28, 'F')
    doc.setFillColor(59, 130, 246)
    doc.rect(0, 28, pageWidth, 4, 'F')

    doc.setTextColor(255, 255, 255)
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(18)
    doc.text('Relatório de Participantes', marginX, 18)
    doc.setFontSize(12)
    doc.setFont('helvetica', 'normal')
    doc.text(evento.titulo, marginX, 24)

    const badgeWidth = 52
    doc.setFillColor(255, 255, 255)
    doc.setTextColor(36, 99, 235)
    doc.roundedRect(pageWidth - marginX - badgeWidth, 10, badgeWidth, 14, 3, 3, 'F')
    doc.setFontSize(9)
    doc.text(
      `Gerado em\n${new Date().toLocaleString('pt-BR')}`,
      pageWidth - marginX - badgeWidth / 2,
      15,
      { align: 'center', baseline: 'middle' }
    )

    doc.setTextColor(55, 65, 81)
  }

  const drawFooter = (pageNumber: number) => {
    doc.setDrawColor(226, 232, 240)
    doc.line(marginX, pageHeight - 20, pageWidth - marginX, pageHeight - 20)
    doc.setFontSize(8)
    doc.setTextColor(148, 163, 184)
    doc.text(`Relatório de Participantes • ${evento.titulo}`, marginX, pageHeight - 14)
    doc.text(`Página ${pageNumber}`, pageWidth - marginX, pageHeight - 14, { align: 'right' })
    doc.setTextColor(55, 65, 81)
  }

  const addEventSummary = (startY: number) => {
    doc.setFillColor(241, 245, 249)
    doc.roundedRect(marginX, startY, pageWidth - marginX * 2, 32, 3, 3, 'F')
    doc.setFontSize(12)
    doc.setFont('helvetica', 'bold')
    doc.text('Resumo do Evento', marginX + 4, startY + 8)

    doc.setFontSize(10)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(75, 85, 99)

    const lines = [
      `Período: ${formatDate(evento.inicio_em, true)} até ${
        fimEm ? formatDate(fimEm, true) : 'A definir'
      }`,
      `Local: ${evento.local ?? 'Não informado'}`,
      `Capacidade: ${evento.capacidade ?? 'Não definida'} • Confirmados: ${stats.confirmados} • Pendentes: ${stats.pendentes} • Cancelados: ${stats.cancelados}`,
      `Receita recebida: ${formatCurrency(stats.receitaRecebida)} • Em aberto: ${formatCurrency(
        stats.receitaPendente
      )}`,
    ]

    let currentY = startY + 14
    lines.forEach((line) => {
      doc.text(line, marginX + 8, currentY)
      currentY += 6
    })

    doc.setTextColor(55, 65, 81)
    return startY + 34
  }

  const addParticipantsTable = (startY: number) => {
    const columnHeaders: { label: string; width: number }[] = [
      { label: 'Participante', width: 48 },
      { label: 'Contato', width: 45 },
      { label: 'Status', width: 24 },
      { label: 'Pagamento', width: 32 },
      { label: 'Check-in', width: 25 },
    ]

    const columnPositions: number[] = []
    columnHeaders.forEach((_header, index) => {
      const position =
        index === 0 ? marginX : columnPositions[index - 1] + columnHeaders[index - 1].width
      columnPositions.push(position)
    })

    doc.setFontSize(11)
    doc.setFont('helvetica', 'bold')
    doc.text('Participantes', marginX, startY)
    doc.setFontSize(9)
    doc.setFont('helvetica', 'bold')

    const headerY = startY + 8
    doc.setFillColor(236, 254, 255)
    doc.roundedRect(marginX, headerY - 5, pageWidth - marginX * 2, 12, 3, 3, 'F')
    doc.setTextColor(0, 112, 243)

    columnHeaders.forEach(({ label }, index) => {
      doc.text(label, columnPositions[index] + 3, headerY + 1)
    })
    doc.setTextColor(55, 65, 81)
    doc.line(marginX, headerY + 5, pageWidth - marginX, headerY + 5)

    doc.setFont('helvetica', 'normal')
    let currentY = headerY + 11
    let pageNumber = 1

    participantes.forEach((participante, index) => {
      if (currentY > pageHeight - 30) {
        doc.addPage()
        pageNumber += 1
        drawHeader()
        drawFooter(pageNumber)
        currentY = addEventSummary(headerHeight + 12) + 8
        doc.setFontSize(11)
        doc.setFont('helvetica', 'bold')
        doc.text('Participantes (continuação)', marginX, currentY)
        doc.setFontSize(9)
        doc.setFont('helvetica', 'bold')
        const continuationHeaderY = currentY + 6
        doc.setFillColor(236, 254, 255)
        doc.roundedRect(marginX, continuationHeaderY - 5, pageWidth - marginX * 2, 12, 3, 3, 'F')
        doc.setTextColor(0, 112, 243)
        columnHeaders.forEach(({ label }, columnIndex) => {
          doc.text(label, columnPositions[columnIndex] + 3, continuationHeaderY + 1)
        })
        doc.setTextColor(55, 65, 81)
        doc.line(marginX, continuationHeaderY + 5, pageWidth - marginX, continuationHeaderY + 5)
        doc.setFont('helvetica', 'normal')
        currentY = continuationHeaderY + 11
      }

      const contato = `${participante.email}\n${participante.telefone}`
      const pagamento = `${participante.pagamento.status === 'pago' ? 'Pago' : participante.pagamento.status === 'pendente' ? 'Pendente' : 'Isento'}${
        participante.pagamento.metodo
          ? ` (${METODO_PAGAMENTO_LABEL[participante.pagamento.metodo]})`
          : ''
      }\n${formatCurrency(participante.pagamento.valor)}`
      const checkIn =
        participante.checkIn.status === 'realizado'
          ? participante.checkIn.horario
            ? `Realizado (${new Date(participante.checkIn.horario).toLocaleTimeString('pt-BR', {
                hour: '2-digit',
                minute: '2-digit',
              })})`
            : 'Realizado'
          : 'Pendente'

      const participantLines = doc.splitTextToSize(
        `${participante.nome}${participante.funcoes.length ? ` • ${participante.funcoes.join(', ')}` : ''}`,
        columnHeaders[0].width - 6
      )
      const contactLines = doc.splitTextToSize(contato, columnHeaders[1].width - 6)
      const statusLines = doc.splitTextToSize(
        participante.status === 'confirmado'
          ? 'Confirmado'
          : participante.status === 'pendente'
            ? 'Pendente'
            : 'Cancelado',
        columnHeaders[2].width - 6
      )
      const paymentLines = doc.splitTextToSize(pagamento.trim(), columnHeaders[3].width - 6)
      const checkInLines = doc.splitTextToSize(checkIn, columnHeaders[4].width - 6)

      doc.setFontSize(9)
      doc.setTextColor(55, 65, 81)
      doc.text(participantLines, columnPositions[0] + 3, currentY, { baseline: 'top' })
      doc.setTextColor(100, 116, 139)
      doc.text(contactLines, columnPositions[1] + 3, currentY, { baseline: 'top' })
      doc.setTextColor(55, 65, 81)
      doc.text(statusLines, columnPositions[2] + 3, currentY, { baseline: 'top' })
      doc.setTextColor(30, 64, 175)
      doc.text(paymentLines, columnPositions[3] + 3, currentY, { baseline: 'top' })
      doc.setTextColor(55, 65, 81)
      doc.text(checkInLines, columnPositions[4] + 3, currentY, { baseline: 'top' })

      const linesNeeded = Math.max(
        participantLines.length,
        contactLines.length,
        statusLines.length,
        paymentLines.length,
        checkInLines.length
      )
      currentY += 6 + (linesNeeded - 1) * 4

      if (index < participantes.length - 1) {
        doc.setDrawColor(226, 232, 240)
        doc.line(marginX, currentY - 2, pageWidth - marginX, currentY - 2)
        doc.setDrawColor(0)
      }
    })

    return pageNumber
  }

  drawHeader()
  const nextY = addEventSummary(headerHeight + 12)
  const totalPages = addParticipantsTable(nextY + 6)
  drawFooter(totalPages)

  const fileName = `${getEventoSlug(evento)}-participantes.pdf`
  doc.save(fileName)
}
